/**
 * NotificationService - Manages push notifications for reminders
 * Uses the Notification API with Service Worker for background notifications (Web)
 * On Android native, delegates to FCM via backend
 */

import { isNativeAndroid, isWeb } from '../utils/platform';

const NOTIFICATION_STORAGE_KEY = 'myboki_scheduled_notifications';

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.scheduledTimers = new Map();
    this._isAndroid = null; // Cache for platform detection
    // Defer init to avoid issues during module loading
    setTimeout(() => this.init(), 0);
  }

  /**
   * Check if running on Android - with caching and error handling
   */
  checkIsAndroid() {
    if (this._isAndroid !== null) {
      return this._isAndroid;
    }
    try {
      this._isAndroid = isNativeAndroid();
    } catch (e) {
      this._isAndroid = false;
    }
    return this._isAndroid;
  }

  /**
   * Initialize the notification service
   */
  async init() {
    const isAndroid = this.checkIsAndroid();

    // On Android, notifications are handled by FCM via backend
    if (isAndroid) {
      console.log('[NotificationService] Running on Android - using FCM via backend');
      return;
    }

    // Web notification setup
    if (!('Notification' in window)) {
      console.warn('[NotificationService] Notifications not supported');
      return;
    }

    this.permission = Notification.permission;

    // Restore scheduled notifications from storage
    this.restoreScheduledNotifications();

    // Listen for visibility changes to reschedule if needed
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.restoreScheduledNotifications();
      }
    });
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    // On Android, always return true (FCM handles it)
    if (this.checkIsAndroid()) {
      return true;
    }
    try {
      return 'Notification' in window && 'serviceWorker' in navigator;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get current permission status
   */
  getPermission() {
    // On Android, FCM handles permissions
    if (this.checkIsAndroid()) {
      return 'granted';
    }
    try {
      if (typeof Notification !== 'undefined' && Notification.permission) {
        return Notification.permission;
      }
    } catch (e) {
      // Notification API not available
    }
    return 'denied';
  }

  /**
   * Request notification permission from user
   * @returns {Promise<string>} Permission status: 'granted', 'denied', or 'default'
   */
  async requestPermission() {
    // On Android, FCM handles permissions via Capacitor plugin
    if (this.checkIsAndroid()) {
      return 'granted';
    }

    if (!this.isSupported()) {
      console.warn('[NotificationService] Notifications not supported');
      return 'denied';
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission;
    } catch (error) {
      console.error('[NotificationService] Error requesting permission:', error);
      return 'denied';
    }
  }

  /**
   * Schedule a notification for a reminder
   * @param {Object} reminder - The reminder object
   * @returns {boolean} Success status
   */
  scheduleNotification(reminder) {
    // On Android, notifications are handled by backend scheduler + FCM
    if (this.checkIsAndroid()) {
      console.log('[NotificationService] Android: notification will be sent via FCM by backend');
      return true;
    }

    if (!this.isSupported() || this.permission !== 'granted') {
      console.warn('[NotificationService] Cannot schedule - permission not granted');
      return false;
    }

    const targetTime = new Date(reminder.snoozedUntil || reminder.nextAt).getTime();
    const now = Date.now();
    const delay = targetTime - now;

    if (delay <= 0) {
      // Time already passed, show immediately
      this.showNotification(reminder);
      return true;
    }

    // Clear existing timer for this reminder
    this.clearScheduledNotification(reminder.id);

    // Schedule the notification
    const timerId = setTimeout(() => {
      this.showNotification(reminder);
      this.scheduledTimers.delete(reminder.id);
      this.removeFromStorage(reminder.id);
    }, delay);

    this.scheduledTimers.set(reminder.id, timerId);

    // Persist to storage for recovery after page reload
    this.saveToStorage(reminder);

    console.log(`[NotificationService] Scheduled notification for "${reminder.title}" in ${Math.round(delay / 1000)}s`);
    return true;
  }

  /**
   * Show a notification immediately
   * @param {Object} reminder - The reminder object
   */
  async showNotification(reminder) {
    // On Android, notifications are sent via FCM
    if (this.checkIsAndroid()) {
      console.log('[NotificationService] Android: cannot show local notification, use FCM');
      return;
    }

    if (!this.isSupported() || this.permission !== 'granted') {
      return;
    }

    const options = {
      body: this.buildNotificationBody(reminder),
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: `reminder-${reminder.id}`,
      renotify: true,
      requireInteraction: true,
      vibrate: [200, 100, 200],
      data: {
        reminderId: reminder.id,
        bookmarkUrl: reminder.bookmarkUrl,
        bookmarkId: reminder.bookmarkId,
        url: reminder.bookmarkUrl || '/'
      },
      actions: [
        { action: 'open', title: 'Öffnen' },
        { action: 'snooze', title: '10 Min später' },
        { action: 'dismiss', title: 'Schließen' }
      ]
    };

    try {
      // Try to use Service Worker for background notifications
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(reminder.title || 'MyBoki Erinnerung', options);
      console.log(`[NotificationService] Showed notification: "${reminder.title}"`);
    } catch (error) {
      // Fallback to regular Notification API
      console.warn('[NotificationService] SW notification failed, using fallback:', error);
      const notification = new Notification(reminder.title || 'MyBoki Erinnerung', options);

      notification.onclick = () => {
        window.focus();
        if (reminder.bookmarkUrl) {
          window.open(reminder.bookmarkUrl, '_blank');
        }
        notification.close();
      };
    }
  }

  /**
   * Build notification body text
   */
  buildNotificationBody(reminder) {
    const parts = [];

    if (reminder.bookmarkTitle) {
      parts.push(`📖 ${reminder.bookmarkTitle}`);
    }

    if (reminder.bookmarkUrl) {
      try {
        const domain = new URL(reminder.bookmarkUrl).hostname.replace('www.', '');
        parts.push(`🔗 ${domain}`);
      } catch {
        // Ignore invalid URL
      }
    }

    if (parts.length === 0) {
      parts.push('Zeit für deine Erinnerung!');
    }

    return parts.join('\n');
  }

  /**
   * Clear a scheduled notification
   * @param {string} reminderId - The reminder ID
   */
  clearScheduledNotification(reminderId) {
    if (this.scheduledTimers.has(reminderId)) {
      clearTimeout(this.scheduledTimers.get(reminderId));
      this.scheduledTimers.delete(reminderId);
      this.removeFromStorage(reminderId);
      console.log(`[NotificationService] Cleared notification for reminder ${reminderId}`);
    }
  }

  /**
   * Clear all scheduled notifications
   */
  clearAllScheduledNotifications() {
    this.scheduledTimers.forEach((timerId) => clearTimeout(timerId));
    this.scheduledTimers.clear();
    localStorage.removeItem(NOTIFICATION_STORAGE_KEY);
    console.log('[NotificationService] Cleared all scheduled notifications');
  }

  /**
   * Save scheduled notification to localStorage for persistence
   */
  saveToStorage(reminder) {
    try {
      const stored = JSON.parse(localStorage.getItem(NOTIFICATION_STORAGE_KEY) || '{}');
      stored[reminder.id] = {
        id: reminder.id,
        title: reminder.title,
        nextAt: reminder.nextAt,
        snoozedUntil: reminder.snoozedUntil,
        bookmarkId: reminder.bookmarkId,
        bookmarkUrl: reminder.bookmarkUrl,
        bookmarkTitle: reminder.bookmarkTitle
      };
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(stored));
    } catch (error) {
      console.error('[NotificationService] Error saving to storage:', error);
    }
  }

  /**
   * Remove notification from localStorage
   */
  removeFromStorage(reminderId) {
    try {
      const stored = JSON.parse(localStorage.getItem(NOTIFICATION_STORAGE_KEY) || '{}');
      delete stored[reminderId];
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(stored));
    } catch (error) {
      console.error('[NotificationService] Error removing from storage:', error);
    }
  }

  /**
   * Restore scheduled notifications from localStorage
   */
  restoreScheduledNotifications() {
    // On Android, notifications are handled by backend
    if (this.checkIsAndroid()) {
      return;
    }

    try {
      const stored = JSON.parse(localStorage.getItem(NOTIFICATION_STORAGE_KEY) || '{}');
      const now = Date.now();

      Object.values(stored).forEach((reminder) => {
        const targetTime = new Date(reminder.snoozedUntil || reminder.nextAt).getTime();

        if (targetTime > now) {
          // Still in the future, reschedule
          this.scheduleNotification(reminder);
        } else {
          // Time passed, remove from storage
          this.removeFromStorage(reminder.id);
        }
      });
    } catch (error) {
      console.error('[NotificationService] Error restoring notifications:', error);
    }
  }

  /**
   * Get count of scheduled notifications
   */
  getScheduledCount() {
    return this.scheduledTimers.size;
  }

  /**
   * Check if a reminder has a scheduled notification
   */
  isScheduled(reminderId) {
    return this.scheduledTimers.has(reminderId);
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;
export { NotificationService };
